import { FC, useCallback, useMemo } from 'react';
import { Alert, Button, Form, Input, Select, Typography } from 'antd';
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { ShippingTracker } from '#types';
import FlexCol from '@/components/atoms/FlexCol';
import FlexBox from '@/components/atoms/FlexBox';
import FlexDivider from '@/components/atoms/FlexDivider';
import { shippingCarriers } from './shippingCarriers';
import TrackingHistoryTimeline from './TrackingHistoryTimeline';

const { Text } = Typography;

interface DetailedTrackingFormProps {
    trackerValue: ShippingTracker;
    disabled?: boolean;
    status?: '' | 'error' | 'warning' | undefined;
    onComponentChange: (components: ShippingTracker['components']) => void;
    onRefresh?: (params: { trackingNumber: string; carrierId: string; force?: boolean }) => Promise<void>;
    isRefreshing?: boolean;
    refreshError?: string | null;
    onCancel?: () => void;
}

const DetailedTrackingForm: FC<DetailedTrackingFormProps> = ({
    trackerValue,
    disabled = false,
    status,
    onComponentChange,
    onRefresh,
    isRefreshing = false,
    refreshError,
    onCancel
}) => {
    const [form] = Form.useForm();

    const initialValues = useMemo(() => {
        const components = trackerValue?.components;
        return {
            carrierId: components?.carrierId || 'other',
            trackingNumber: components?.trackingNumber || '',
        };
    }, [trackerValue]);

    const carrierId = Form.useWatch('carrierId', form);
    const trackingNumber = Form.useWatch('trackingNumber', form);

    const carrier = useMemo(
        () => shippingCarriers.find((c) => c.id === carrierId) || shippingCarriers.find((c) => c.id === 'other'),
        [carrierId]
    );
    const trackingUrl = useMemo(() => {
        const tn = (trackingNumber || '').trim();
        if (!tn) return undefined;
        const template = carrier?.trackingUrlTemplate;
        return template ? template.replace('{trackingNumber}', tn) : undefined;
    }, [carrier?.trackingUrlTemplate, trackingNumber]);

    const handleSaveTrackingId = useCallback(async () => {
        const values = await form.validateFields();

        const tn = (values.trackingNumber || '').trim();
        const cid = values.carrierId || 'other';

        const components: ShippingTracker['components'] = {
            trackingNumber: tn,
            carrierId: cid,
            // EasyPost is the source of truth for these; clear any stale snapshot when the tracking id changes.
            currentStatus: undefined,
            deliveryDate: undefined,
            origin: undefined,
            destination: undefined,
            trackingHistory: undefined,
        };

        onComponentChange(components);
    }, [form, onComponentChange]);

    const handleRefresh = useCallback(async () => {
        if (!onRefresh) return;
        const values = await form.validateFields();
        const tn = (values.trackingNumber || '').trim();
        const cid = values.carrierId || 'other';
        await onRefresh({ trackingNumber: tn, carrierId: cid, force: true });
    }, [form, onRefresh]);

    const readOnly = trackerValue?.components;
    const deliveryDateText = readOnly?.deliveryDate instanceof Timestamp
        ? dayjs(readOnly.deliveryDate.toDate()).format('MMM D, YYYY')
        : undefined;

    const lastSyncedAtText = useMemo(() => {
        const ts = (trackerValue?.meta as any)?.easypost?.lastSyncedAt;
        if (ts instanceof Timestamp) return dayjs(ts.toDate()).format('MMM D, YYYY h:mm A');
        return undefined;
    }, [trackerValue?.meta]);

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            key={trackerValue?.formatted || 'shipping-tracker'}
        >
            <Form.Item name="carrierId" label="Carrier">
                <Select
                    disabled={disabled}
                    status={status}
                    aria-label="carrier-select"
                    options={shippingCarriers.map((c) => ({ label: c.name, value: c.id }))}
                />
            </Form.Item>

            <Form.Item
                name="trackingNumber"
                label="Tracking Number"
                rules={[
                    {
                        transform: (v) => (typeof v === 'string' ? v.trim() : v),
                        max: 128,
                        message: 'Tracking number is too long',
                    }
                ]}
            >
                <Input
                    disabled={disabled}
                    status={status}
                    placeholder="Enter tracking number"
                    aria-label="tracking-number-input"
                    autoComplete="off"
                />
            </Form.Item>

            {trackingUrl ? (
                <FlexBox>
                    <Button
                        size="small"
                        icon={<LinkOutlined />}
                        href={trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="open-tracking-link"
                    >
                        Track on {carrier?.name || 'carrier site'}
                    </Button>
                </FlexBox>
            ) : null}

            <FlexDivider spacing={10} />

            <FlexCol gap={8} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    EasyPost snapshot {lastSyncedAtText ? `· last synced ${lastSyncedAtText}` : ''}
                </Text>

                {refreshError ? (
                    <Alert
                        type="error"
                        showIcon
                        title="EasyPost sync failed"
                        description={refreshError}
                    />
                ) : null}

                <FlexCol noGap style={{ width: '100%' }}>
                    <FlexBox style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                        <Text>{readOnly?.currentStatus || '—'}</Text>
                    </FlexBox>
                    <FlexBox style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Delivery date</Text>
                        <Text>{deliveryDateText || '—'}</Text>
                    </FlexBox>
                    {readOnly?.origin ? (
                        <FlexBox style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Origin</Text>
                            <Text>{readOnly.origin}</Text>
                        </FlexBox>
                    ) : null}
                    {readOnly?.destination ? (
                        <FlexBox style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Destination</Text>
                            <Text>{readOnly.destination}</Text>
                        </FlexBox>
                    ) : null}
                </FlexCol>

                <FlexDivider spacing={10} />

                <Text>Tracking History</Text>
                <TrackingHistoryTimeline trackingHistory={readOnly?.trackingHistory} />
            </FlexCol>

            <FlexDivider spacing={10} />

            <FlexCol style={{ width: '100%' }}>
                <Button
                    type="primary"
                    onClick={() => void handleSaveTrackingId()}
                    disabled={disabled}
                    style={{ width: '100%' }}
                    aria-label="update-tracking-info"
                >
                    Save tracking info
                </Button>

                {onRefresh ? (
                    <Button
                        onClick={() => void handleRefresh()}
                        icon={<ReloadOutlined />}
                        loading={isRefreshing}
                        disabled={disabled}
                        style={{ width: '100%' }}
                        aria-label="refresh-from-easypost"
                    >
                        Refresh from EasyPost
                    </Button>
                ) : null}

                {onCancel ? (
                    <Button
                        type="link"
                        danger
                        onClick={onCancel}
                        disabled={disabled}
                        style={{ width: '100%' }}
                        aria-label="cancel-tracking-edit"
                    >
                        Cancel
                    </Button>
                ) : null}
            </FlexCol>
        </Form>
    );
};

export default DetailedTrackingForm;

